import { useCallback, useMemo, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { InkBlock } from '@/components/atoms/InkBlock';
import { SectionHeader } from '@/components/molecules/SectionHeader';
import { cn } from '@/lib/utils';
import type { Character, MoveRef } from '@/schemas/character';
import type { ClassDefinition } from '@/schemas/classDefinition';
import { hasMoveSetup, type Move, type Prerequisite } from '@/schemas/move';
import { findMoveByIdGlobal } from '@/data/moves';
import { useExpandedState } from '@/hooks/useExpandedState';
import { MoveCard } from '@/components/molecules/MoveCard';
import { MoveSetupPanel } from '../../sections/MoveSetupPanel';
import { isMoveSetupComplete } from '../../services/moveSetupComplete';
import {
  updateMoveChoiceGroup,
  updateMoveNarrativeField,
  updateMoveRecruitOutcome,
} from '../../services/moveRefMutations';

// page-specific (Fase 2 PR 8): step «scegli una mossa» del flusso salita di
// livello. Pixel-parity dai pattern mockup `.lvup-move-card` + `.meta-chip`
// (styles.css mockup righe 1987-2016). Riusa `<MoveCard>` (molecule shared
// promossa in PR 11 da `ExpandableMoveRow` page-specific PR 6/8).
//
// Manuale V1 r. 1756: «scegli una nuova mossa avanzata della tua classe o
// una mossa comune disponibile al tuo nuovo livello». Il service
// `getAvailableAdvancedMoves` / `getAvailableCommonMoves` già filtra le
// mosse non prendibili — qui mostriamo solo quelle effettivamente
// selezionabili. La pillola "Sostituisce" appare se la mossa ha `replaces`.
//
// **PR 8 cambio API**: i bottoni Indietro/Avanti sono delegati a
// `<StepNav>` esterno renderizzato da `LevelUpFlowPage`. Le prop `onNext`/
// `onBack` rimosse.

interface LevelUpStepMoveProps {
  character: Character;
  classDef: ClassDefinition;
  targetLevel: number;
  moveAdded: MoveRef | undefined;
  onMoveChange: (moveAdded: MoveRef | undefined) => void;
  getAvailableAdvancedMoves: () => readonly Move[];
  getAvailableCommonMoves: () => readonly Move[];
  getReplacedMoves: (move: Move) => readonly MoveRef[];
}

function isSelectedMoveSetupComplete(
  move: Move,
  moveRef: MoveRef,
  archetypeId: string | undefined,
): boolean {
  if (!hasMoveSetup(move)) return true;
  return isMoveSetupComplete(move, moveRef, archetypeId);
}

export function LevelUpStepMove(props: LevelUpStepMoveProps) {
  const { character, classDef, targetLevel, moveAdded, onMoveChange } = props;
  const advanced = props.getAvailableAdvancedMoves();
  const common = props.getAvailableCommonMoves();

  const allMoveIds = useMemo(() => [...advanced, ...common].map((m) => m.id), [advanced, common]);
  const expandState = useExpandedState(allMoveIds);

  // FI-79: ownedMoveIds del PG per disabilitare spell-options con
  // `requiresMove` non soddisfatto (es. Curare Ferite Moderate richiede
  // Primo Soccorso Migliorato). Coerente con builder L1.
  const ownedMoveIds = useMemo(() => character.moves.map((m) => m.id), [character.moves]);

  // FI-75: setup pannello inline alla MoveCard della mossa selezionata.
  // Disponibile SOLO per la mossa attualmente selezionata (moveAdded.id === move.id).
  const renderSetupSlot = useCallback(
    (move: Move): ReactNode => {
      if (moveAdded?.id !== move.id || !hasMoveSetup(move)) return null;
      return (
        <MoveSetupPanel
          move={move}
          selectedByGroup={buildSelectedByGroup(move, moveAdded)}
          narrativeValues={moveAdded.narrativeFieldValues ?? {}}
          recruitOutcome={moveAdded.recruitOutcome}
          archetypeId={character.archetypeId}
          ownedMoveIds={ownedMoveIds}
          onChoiceGroupChange={(moveId, groupId, selectedIds) => {
            onMoveChange(updateMoveChoiceGroup(moveAdded, moveId, groupId, selectedIds));
          }}
          onNarrativeFieldChange={(moveId, fieldId, value) => {
            onMoveChange(updateMoveNarrativeField(moveAdded, moveId, fieldId, value));
          }}
          onRecruitOutcomeChange={(moveId, outcome) => {
            onMoveChange(updateMoveRecruitOutcome(moveAdded, moveId, outcome));
          }}
        />
      );
    },
    [moveAdded, character.archetypeId, ownedMoveIds, onMoveChange],
  );

  return (
    <section>
      <SectionHeader
        title="Scegli una mossa"
        sub={`Manuale V1 r. 1756: scegli una nuova mossa avanzata della tua classe (${classDef.name}) o una mossa comune disponibile al tuo nuovo livello (L${targetLevel}).`}
      />
      <div className="mt-4 space-y-5">
        <ExpandAllToolbar hasMoves={allMoveIds.length > 0} expandState={expandState} />
        <MovesLists
          classDef={classDef}
          targetLevel={targetLevel}
          advanced={advanced}
          common={common}
          moveAdded={moveAdded}
          archetypeId={character.archetypeId}
          getReplacedMoves={props.getReplacedMoves}
          expandState={expandState}
          onSelect={(move) => onMoveChange(buildNewMoveRef(move))}
          renderSetupSlot={renderSetupSlot}
        />
      </div>
    </section>
  );
}

/**
 * Proietta un `MoveRef` in `selectedByGroup` (mappa `groupId → ids`) per
 * `MoveSetupPanel`. Il gruppo virtuale `'main'` è popolato da
 * `selectedChoices` (shorthand `choices`), gli altri da
 * `selectedChoiceGroups` (multi-group). Coerente con la proiezione fatta
 * dal builder L1 (`SectionStartingMoves.buildMoveRef`) in senso inverso.
 */
function buildSelectedByGroup(move: Move, moveRef: MoveRef): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  if (move.choices) result.main = [...moveRef.selectedChoices];
  if (moveRef.selectedChoiceGroups) {
    for (const [groupId, ids] of Object.entries(moveRef.selectedChoiceGroups)) {
      result[groupId] = [...ids];
    }
  }
  return result;
}

function buildNewMoveRef(move: Move): MoveRef {
  return {
    id: move.id,
    selectedChoices: [],
    ...(move.choiceGroups ? { selectedChoiceGroups: {} } : {}),
    ...(move.narrativeFields ? { narrativeFieldValues: {} } : {}),
  };
}

interface ExpandAllToolbarProps {
  hasMoves: boolean;
  expandState: ReturnType<typeof useExpandedState>;
}

function ExpandAllToolbar({ hasMoves, expandState }: ExpandAllToolbarProps) {
  if (!hasMoves) return null;
  const { allExpanded, expandAll, collapseAll } = expandState;
  return (
    <div className="flex justify-end">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={allExpanded ? collapseAll : expandAll}
      >
        {allExpanded ? 'Comprimi tutte' : 'Espandi tutte'}
      </Button>
    </div>
  );
}

interface MovesListsProps {
  classDef: ClassDefinition;
  targetLevel: number;
  advanced: readonly Move[];
  common: readonly Move[];
  moveAdded: MoveRef | undefined;
  archetypeId: string | undefined;
  getReplacedMoves: (move: Move) => readonly MoveRef[];
  expandState: ReturnType<typeof useExpandedState>;
  onSelect: (move: Move) => void;
  renderSetupSlot: (move: Move) => ReactNode;
}

function MovesLists({
  classDef,
  targetLevel,
  advanced,
  common,
  moveAdded,
  archetypeId,
  getReplacedMoves,
  expandState,
  onSelect,
  renderSetupSlot,
}: MovesListsProps) {
  return (
    <>
      <MoveList
        title={`Mosse avanzate ${classDef.name} (L${targetLevel})`}
        count={advanced.length}
        moves={advanced}
        selectedId={moveAdded?.id}
        selectedMoveRef={moveAdded}
        archetypeId={archetypeId}
        onSelect={onSelect}
        getReplacedMoves={getReplacedMoves}
        expandState={expandState}
        renderSetupSlot={renderSetupSlot}
      />
      {common.length > 0 && (
        <MoveList
          title="Mosse comuni disponibili"
          count={common.length}
          moves={common}
          selectedId={moveAdded?.id}
          selectedMoveRef={moveAdded}
          archetypeId={archetypeId}
          onSelect={onSelect}
          getReplacedMoves={getReplacedMoves}
          expandState={expandState}
          renderSetupSlot={renderSetupSlot}
        />
      )}
    </>
  );
}

interface MoveListProps {
  title: string;
  count: number;
  moves: readonly Move[];
  selectedId: string | undefined;
  selectedMoveRef: MoveRef | undefined;
  archetypeId: string | undefined;
  onSelect: (move: Move) => void;
  getReplacedMoves: (move: Move) => readonly MoveRef[];
  expandState: ReturnType<typeof useExpandedState>;
  renderSetupSlot: (move: Move) => ReactNode;
}

function MoveList({
  title,
  count,
  moves,
  selectedId,
  selectedMoveRef,
  archetypeId,
  onSelect,
  getReplacedMoves,
  expandState,
  renderSetupSlot,
}: MoveListProps) {
  if (moves.length === 0) {
    return (
      <div>
        <ListHeader title={title} hint="Nessuna mossa disponibile." />
      </div>
    );
  }
  const { expanded, toggleExpanded } = expandState;
  return (
    <div className="space-y-2">
      <ListHeader title={title} hint={`${count} disponibili`} />
      <RadioGroup value={selectedId ?? ''} className="gap-2">
        {moves.map((move) => (
          <MoveRow
            key={move.id}
            move={move}
            isSelected={selectedId === move.id}
            selectedMoveRef={selectedMoveRef}
            archetypeId={archetypeId}
            replaced={getReplacedMoves(move)}
            expanded={expanded.has(move.id)}
            onToggle={() => toggleExpanded(move.id)}
            onSelect={() => onSelect(move)}
            renderSetupSlot={renderSetupSlot}
          />
        ))}
      </RadioGroup>
    </div>
  );
}

function ListHeader({ title, hint }: { title: string; hint: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <InkBlock variant="full" className="flex-1">
        {title}
      </InkBlock>
      <em className="text-ink-soft font-serif text-[0.85rem] whitespace-nowrap">{hint}</em>
    </div>
  );
}

interface MoveRowProps {
  move: Move;
  isSelected: boolean;
  selectedMoveRef: MoveRef | undefined;
  archetypeId: string | undefined;
  replaced: readonly MoveRef[];
  expanded: boolean;
  onToggle: () => void;
  onSelect: () => void;
  renderSetupSlot: (move: Move) => ReactNode;
}

function MoveRow({
  move,
  isSelected,
  selectedMoveRef,
  archetypeId,
  replaced,
  expanded,
  onToggle,
  onSelect,
  renderSetupSlot,
}: MoveRowProps) {
  const showCheck =
    isSelected &&
    selectedMoveRef !== undefined &&
    isSelectedMoveSetupComplete(move, selectedMoveRef, archetypeId);
  const inputId = `lvlup-move-${move.id}`;
  const isMulti = move.tags.includes('multiclasse');
  const meta = buildMoveMeta(move, replaced);
  return (
    <MoveCard
      move={move}
      fallbackId={move.id}
      expanded={expanded}
      onToggle={onToggle}
      labelHtmlFor={inputId}
      selected={isSelected}
      setupComplete={showCheck}
      multi={isMulti}
      {...(meta ? { meta } : {})}
      leading={<RadioGroupItem value={move.id} id={inputId} className="mt-1" onClick={onSelect} />}
      setupSlot={isSelected ? renderSetupSlot(move) : null}
    />
  );
}

/**
 * Risolve l'id mossa → nome leggibile cercando in tutto il catalogo (12
 * classi + comuni). Necessario `findMoveByIdGlobal` (non `*ByIdAnyScope`)
 * perché i prereq cross-class esistono nel modello DWR — es. *Cimelio*
 * del Guerriero ha `anyOf: ['guerriero.arma-prescelta',
 * 'warlock.vincolo-della-lama']`. Fallback all'id grezzo solo se l'id
 * non esiste in nessun catalogo. Pattern coerente con la memoria utente
 * `feedback_user_facing_copy`: i testi UI parlano linguaggio utente, mai
 * ID schema-stile.
 */
function moveNameForId(moveId: string): string {
  return findMoveByIdGlobal(moveId)?.name ?? moveId;
}

function buildMoveMeta(move: Move, replaced: readonly MoveRef[]): ReactNode {
  const chips: ReactNode[] = [];
  if (replaced.length > 0) {
    chips.push(
      <MetaChip key="replaces" variant="replace">
        Sostituisce: {replaced.map((m) => moveNameForId(m.id)).join(', ')}
      </MetaChip>,
    );
  }
  const prereqLabel = formatPrerequisites(move.prerequisites);
  if (prereqLabel) {
    chips.push(<MetaChip key="prereq">Richiede: {prereqLabel}</MetaChip>);
  }
  if (chips.length === 0) return null;
  return <>{chips}</>;
}

function formatPrerequisites(prereqs: readonly Prerequisite[]): string {
  if (prereqs.length === 0) return '';
  return prereqs
    .map((p) => {
      if (typeof p === 'string') return moveNameForId(p);
      if ('allOf' in p) return p.allOf.map(moveNameForId).join(' + ');
      return p.anyOf.map(moveNameForId).join(' o ');
    })
    .join(', ');
}

type MetaChipVariant = 'default' | 'replace' | 'reason';
const META_CHIP_VARIANTS: Record<MetaChipVariant, string> = {
  default: 'bg-paper border-rule text-ink-soft',
  replace: 'bg-paper-soft border-rule text-ink-soft',
  reason: 'bg-accent-dwr/10 border-accent-dwr text-accent-dwr',
};

function MetaChip({
  children,
  variant = 'default',
}: {
  children: ReactNode;
  variant?: MetaChipVariant;
}) {
  return (
    <span
      className={cn(
        'font-display inline-flex items-center border px-[0.5em] py-[0.15em] text-[0.65rem] tracking-[0.06em] uppercase',
        META_CHIP_VARIANTS[variant],
      )}
    >
      {children}
    </span>
  );
}
